import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { DataModule } from '@data/data.module';
import { DataService } from '@data/data.service';
import { NotificationModule } from '@notification/notification.module';
import { NotificationService } from '@notification/notification.service';
import { DownloadDocumentComponent } from '@grid-cell/download-document.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { ViewDocumentComponent } from '@grid-cell/view-document.component';
import { FillDocumentComponent } from '../edit/fill-document/fill-document.component';
import { PrintDocumentComponent } from '@grid-cell/print-document.component';
import { EmailDialogButtonComponent } from '@grid-cell/email-dialog-button.component';
import { setTopElementSize, tryToScrollY } from '@util/visual-utils';
import { MyLog } from '@common/common';
import { choice, ensureArray, firstArray, unsub } from '@util/utils';
import { first, interval } from 'rxjs';
import { getClientRect } from '@util/web-utils';
import { DeleteDocumentComponent } from '@grid-cell/delete-document.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CONFIG_KEYS, StateService } from '@services/state.service';
import { FileUploadDialogComponent } from '../file-upload-dialog/file-upload-dialog.component';
import { MediaModule } from '../../media/media.module';
import { MergeDocumentsDialogComponent } from '../merge-documents-dialog/merge-documents-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { UserDeviceService } from '@services/user-device.service';
import { GlobalEventServiceService, GlobalEventType } from '@services/global-event-service.service';
import { OrderDialogService } from '../order-dialog.service';
import { EmailDialogComponent } from '../email/email-dialog/email-dialog.component';
import { ViewDocumentDialogComponent } from '../view/view-document-dialog/view-document-dialog.component';

function isSelected(item: any) {
  return item?.id && item?.selected;
}

function createPosWatcher(checkFn: () => boolean) {
  return interval(50).pipe(first((value: number) => {
    return checkFn();
  }));
}

function ne(elem?: ElementRef) {
  return elem?.nativeElement;
}

@Component({
  selector: 'app-order-documents-grid',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MediaModule,
    DataModule,
    NotificationModule,
    DeleteDocumentComponent,
    DownloadDocumentComponent,
    EmailDialogButtonComponent,
    FillDocumentComponent,
    PrintDocumentComponent,
    ViewDocumentComponent,
  ],
  templateUrl: './order-documents-grid.component.html',
})
export class OrderDocumentsGridComponent implements OnInit, OnDestroy {
  _showTable = true;
  allSelected = false;

  //Normal
  rowDataDocs: any[] = [];

  //Visible by Montagearbeiter
  rowDataStored: any[] = [];

  canFillOrDelete = true;

  dataRequestSub: Subscription | null = null;
  deviceSub!: Subscription;
  elSub?: Subscription | null = null;
  globalSub!: Subscription;

  protected _docWrapper?: ElementRef;
  protected _docTable?: ElementRef;
  protected _docButtons?: ElementRef;

  docPadding = 16;

  @ViewChild('docwrapper') set docwrapper1(wrap: ElementRef) {
    this._docWrapper = wrap;
    this.updateHeightsIf(wrap, 1);
  }

  @ViewChild('doctable') set docTable1(wrap: ElementRef) {
    this._docTable = wrap;
    this.updateHeightsIf(wrap, 1);
  }


  @ViewChild('docbuttons') set docButtons1(wrap: ElementRef) {
    this._docButtons = wrap;
    this.updateHeightsIf(wrap, 1);
  }

  _type = 'provided';
  @Input('type') set type(type: string) {
    this._type = type;
    this.reloadDocs();
  }

  _doctypes: any[] = [];
  @Input('doctypes') set doctypes(doctypes: any[] | undefined) {
    if (doctypes) {
      this._doctypes = doctypes;
    }
  }

  isReadonly = false;
  @Input('readonly') set readonly(readonly: boolean) {
    this.isReadonly = readonly;
  }

  @Input('canmodify') set canmodify(can: boolean) {
    this.canFillOrDelete = can;
  }

  _id: any;
  @Input('id') set id(id: string | number) {
    this._id = id;
    this.reloadDocs();
  }

  updateHeightsIf(condition: any, index: number) {
    condition && this.updateHeights(index);
  }

  clearElSub() {
    this.elSub = unsub(this.elSub);
  }

  updateHeights(tabIdx: number) {

    this.clearElSub();

    this.elSub = createPosWatcher(() => {
      const r1 = getClientRect(ne(this._docWrapper));
      const el2 = ne(this._docTable);
      const r3 = getClientRect(ne(this._docButtons));

      return (r1 && el2 && r3) && r1.top > 0 && r3.height > 0;

    }).subscribe(() => {
      setTopElementSize(
        ne(this._docWrapper),
        ne(this._docTable),
        ne(this._docButtons),
        (this.docPadding + 4) + 26
      );
      this.elSub = unsub(this.elSub, null);
    });
  }

  constructor(
    public matDialog: MatDialog,
    private stateService: StateService,
    private dataService: DataService,
    private deleteService: OrderDialogService,
    public notificationService: NotificationService,
    private userDeviceService: UserDeviceService,
    private globalEventService: GlobalEventServiceService
  ) {
  }

  ngOnInit(): void {
    this.deviceSub = this.userDeviceService.change().subscribe((ev: any) => {
      this.updateHeights(1);
      this.updateHeights(2);
    });

    this.globalSub = this.globalEventService
      .listen('order-email')
      .subscribe((evt: GlobalEventType) => {
        this.extendReceiptDocEntry(evt?.data);
      });
  }

  ngOnDestroy(): void {
    this.clearElSub();
    this.deviceSub = unsub(this.deviceSub);
    this.dataRequestSub = unsub(this.dataRequestSub);
    this.globalSub = unsub(this.globalSub);
  }

  extendReceiptDocEntry(item: any) {
    if (item && item.hasOwnProperty('emailState') && Array.isArray(this.rowDataDocs)) {
      for (let e of this.rowDataDocs) {
        if (e?.type === 'receipt') {
          e.emailState = item.emailState;
          e.emailLogs = item.emailLogs;
        }
      }
    }
  }

  documentSelectChange($event: any, item: any) {
    if (item?.id) {
      item.selected = $event.checked;
    }

    this.updateAllSelectedDocuments();
  }

  addDocument() {
    const params: any = {
      orderId: this._id,
    };

    params.convert = this.stateService.getConfigurationFor(
      CONFIG_KEYS.IMAGE_CONVERT
    );

    params.doctypes = firstArray(this._doctypes, params.doctypes);

    this.matDialog.open(
      FileUploadDialogComponent,
      {
        data: params
      }
    ).afterClosed().subscribe((res: any) => {
      res?.id && this.reloadDocs(res.id);
    });
  }

  selectAllDocuments($event: any) {
    if (Array.isArray(this.rowDataDocs)) {
      const checked = $event.checked;
      this.rowDataDocs.forEach((item: any) => {
        item.selected = checked;
      });
      this.allSelected = checked;
    }
  }

  onDeleteDone(val: any) {
    this.reloadDocs();
  }

  updateAllSelectedDocuments() {
    const allSelected = this.rowDataDocs.every(isSelected);
    if (!allSelected) {
      this.allSelected = false;
    }
  }

  onDocumentEdit($event: any) {
    $event && this.reloadDocs();
  }

  reloadDocs(docId?: any) {
    MyLog.log('Reload:Docs:', this._type, docId);

    if (!this._id) {
      return;
    }
    let url = '', p = '/montage/documents/' + this._id;

    var tabIdx = this._type === 'all' ? 1 : 2;

    if (tabIdx === 1) {
      url = p + '/all';
    }
    if (tabIdx === 2) {
      url = p + '/allprovided';
    }

    if (url) {
      const getTable = () => {
        return ne(this._docTable);
      };

      this.dataRequestSub = this.dataService.get(url).subscribe((res: any) => {
        const result = res?.result;

        if (result?.success) {
          const docs = ensureArray(result.docs);

          if (tabIdx === 1) {
            this.rowDataDocs = docs;
          }

          if (tabIdx === 2) {
            this.rowDataStored = docs;
          }

          this.allSelected = false;

          this.scrollToTableRow(docId, docs, getTable);
          return;
        }

        const err = result?.error || 'Fehler beim Laden';
        this.showErrors([err]);
      });
    }
  }

  scrollToTableRow(
    docId: any,
    docs: any[],
    getTable: () => any,
  ) {
    if (!docId) {
      return;
    }
    const obs: any = {
      cnt: 0,
      intId: null
    };

    obs.intId = setInterval(() => {
      if (obs.cnt > 5) {
        clearInterval(obs.intId);
        return;
      }
      const htmlId = 'doc_row_' + docId;
      const elem = document.getElementById(htmlId);

      const tbl = getTable();
      if (elem && tbl) {

        if (elem.offsetTop > tbl.clientHeight) {
          tryToScrollY(tbl, elem.offsetTop);
          clearInterval(obs.intId);
          const item = docs.find((item: any) => {
            return item.id === docId;
          });

          if (item) {
            item.added = true;
            setTimeout(() => {
              item.added = false;
            }, 2000);
          }
        }
      }
      obs.cnt++;
    }, 100);
  }

  onMediaDone($event: any) {
    $event && this.reloadDocs($event);
  }

  provideTeam() {
    const selected = this.getSelectedDocumentIds();

    if (!selected.length) {
      this.showErrors(['Keine Dokumente ausgewählt']);
      return;
    }

    const data: any = {
      newstate: 1,
      ids: selected
    };

    const url = '/montage/documents/' + this._id + '/0/toggle';

    this.dataRequestSub = this.dataService.post(url, data).subscribe((res: any) => {

      const result = res?.result;

      if (result?.success) {
        this.showSuccess('Sichtbarkeit geändert');

        if (Array.isArray(result.docs)) {
          this.rowDataDocs = result.docs;
          return;
        }
      }
      this.showChangeErrorFromResult(result);
    });
  }

  getSelectedDocuments() {
    return ensureArray(this.rowDataDocs).filter(isSelected);
  }

  getSelectedDocumentIds() {
    return this.getSelectedDocuments().map((item: any) => {
      return item.id;
    });
  }

  mergeDocument() {
    const selected = this.getSelectedDocuments();

    if (selected.length > 1) {
      this.matDialog.open(
        MergeDocumentsDialogComponent,
        {
          data: {
            orderId: this._id,
            files: selected
          },
          maxHeight: '90vh',
          maxWidth: '90vw',
          width: '65vw'
        }
      ).afterClosed().subscribe((res: any) => {
        if (res) {
          const docId = res?.docId;
          this.reloadDocs(docId);
        }
      });
      return;
    }

    this.showErrors(['Es müssen mindestens 2 Dokumente ausgewählt werden']);
  }

  toggleDocumentVisibility(item: any) {
    const data: any = {
      newstate: item.montage_visible ? 0 : 1,
      ids: [item.id],
    };

    const url = '/montage/documents/' + this._id + '/0/toggle';

    this.dataRequestSub = this.dataService
      .post(url, data)
      .subscribe((res: any) => {
        const result = res?.result;

        if (result?.success) {
          this.showSuccess('Sichtbarkeit geändert');

          if (Array.isArray(result.docs)) {
            const elem = result.docs.find((serverItem: any) => {
              return serverItem?.id === item.id;
            });

            if (elem) {
              item.montage_visible = elem.montage_visible;
            }
            return;
          }
        }
        this.showChangeErrorFromResult(result);
      });
  }

  showErrors(errs: any[]) {
    this.notificationService.showErrors(errs);
  }

  showSuccess(msg: any) {
    this.notificationService.showSuccess(msg);
  }

  showChangeErrorFromResult(result: any) {
    const err = result?.error || 'Fehler bei Änderung';
    this.showErrors([err]);
  }

  onDeleteClick(d: any) {

    const sub = this.deleteService.orderChanges().subscribe((data: any) => {
      if (data.cancelled) {
        this.onDeleteDone(false);
        return;
      }
      if (data.success) {
        this.onDeleteDone(true);
        this.notificationService.showSuccess(
          choice(data.msg, 'Dokument gelöscht')
        );
      } else {
        this.onDeleteDone(false);
        this.notificationService.showErrors([data.error]);
      }
      unsub(sub);
    });
    this.deleteService.tryDeleteDocument(false,d.orderId, d.id, d.name);
  }

  onEmailClick(data:any) {

    const dialogData: any = {
      title: 'Rechnungsquittung per E-Mail versenden',
      data: data
      //panelClass: 'my-document-dialog'
    };

    //dialogData.panelClass = 'my-document-dialog';
    //delete dialogData.height;
    return this.matDialog.open(
      EmailDialogComponent,
      {
        data:dialogData,
        maxHeight: '90vh'
      }
    ).afterClosed();
  }

  onViewClick(data:any ){
    this.matDialog.open(ViewDocumentDialogComponent, {
      data: {
        order: data
      },
      panelClass: 'my-dialog-panel',
      position: {
        top: '5vh',
      },
      width: '96vw',
      minWidth: '96vw',
      height: '93vh',
    });
  }
}


